import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { DEVTOOLS_ENABLED } from "../../consts";

// A response body per request, so an unbounded buffer costs real memory on device.
const LOG_BUFFER_LIMIT = 500;

export interface AppLog {
  timeStamp: number;
  message: string;
  tags: string[];
  extraArgs: any[];
}

interface LogsState {
  logs: AppLog[];
  pushLog: (log: AppLog) => void;
}

/**
 * Buffer of in-app developer logs, rendered by the dev logs page.
 *
 * Deliberately single-purpose: `Utils.pushLog` writes to it on *every* log line and
 * `subscribeWithSelector` runs subscribers synchronously inside `set()`, so nothing
 * else should be merged into this store.
 *
 * Uses `create` from zustand directly (not the `resetState` wrapper) so the buffer is
 * not cleared by `resetAllStores()` on logout - matching the previous signal behaviour.
 */
export const useLogsStore = create<LogsState>()(
  subscribeWithSelector(
    devtools(
      (set, get) => ({
        logs: [],
        pushLog: (log) => {
          const next = [...get().logs, log];
          set(
            { logs: next.length > LOG_BUFFER_LIMIT ? next.slice(next.length - LOG_BUFFER_LIMIT) : next },
            false,
            "pushLog",
          );
        },
      }),
      { enabled: DEVTOOLS_ENABLED, name: "logs" },
    ),
  ),
);
